import { push } from 'redux-first-history';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { concatMap, mergeMap } from 'rxjs/operators';
import { isEoe, paths } from '../../common/constants/pathVariables.js';
import { DeviceChangeOption } from '../../common/enums.js';
import { getShoppingBasketFromLocalStorage, parseBasketJson } from '../../common/hooks/useShoppingBasket.js';
import { isCampaignPresent } from '../../common/utils/campaignUtils.js';
import { getTotalSums } from '../../common/utils/commercialProductUtils.js';
import {
  CommercialProductType,
  CreditCheckDecision,
  EppRedeemTerminateRequestType,
  OnlineOrder,
} from '../../generated/api/models.js';
import {
  createEmployeePersonBillingAccountPrivateMethod,
  createOnlineOrderPrivateMethod,
  updateEmployeePersonBillingAccountPrivateMethod,
} from '../../generated/api/uiApiMethods.js';
import {
  TypeKeys,
  pollForCreditCheck,
  pollForCreditCheckFailed,
  pollForCreditCheckV2Fulfilled,
  submitOnlineOrder,
  submitOnlineOrderFailed,
  submitOnlineOrderFulfilled,
  upsertPersonBillingAccountFailed,
  upsertPersonBillingAccountFulfilled,
} from '../actions/index.js';
import { getCatalogCode, getShoppingCartId } from '../common/localStorageUtils.js';
import { callUiApi, getFromUiApiWithRetry, getPrivateUiApiHeaders, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { UI_API_PRIVATE_BASE_PATH, actionToActionState, getMdmIdHeaderFromUrlParams } from './epicUtils.js';
import {
  getDefaultAddOns,
  getSalesProductsOrderItems,
  mapCommercialProductToOnlineOrderItemUnit,
} from './onlineOrderEpicUtils.js';
import { postEppRedeemTerminate } from './subscriptionActionEpic.js';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { UpsertPersonBillingAccountRequest } from '../../common/utils/billingAccountUtils.js';
import type { CreditCheckResponse, OnlineOrderItem, SalesProductOrderItem } from '../../generated/api/models.js';
import type {
  ActionTypes,
  ErrorAction,
  PollForCreditCheckAction,
  PostEppRedeemTerminateAction,
  PostEppRedeemTerminateFulfilledAction,
  SubmitOnlineOrderAction,
  SubmitOrderFulfilledAction,
  UpsertPersonBillingAccountAction,
  UpsertPersonBillingAccountFulfilledAction,
} from '../actions/index.js';
import type { ActionWithId, State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';
import type { Observable } from 'rxjs';
import type { AjaxResponse } from 'rxjs/ajax';

export function upsertPersonBillingAccount(
  request: UpsertPersonBillingAccountRequest,
  epicDependencies: EpicDependencies,
  state$: StateObservable<State>,
  billingAccountId?: string,
  successAction?: () => void
): Observable<UpsertPersonBillingAccountFulfilledAction | ErrorAction<TypeKeys>> {
  return callUiApi({
    epicDependencies,
    state$,
    method: billingAccountId
      ? updateEmployeePersonBillingAccountPrivateMethod()
      : createEmployeePersonBillingAccountPrivateMethod(),
    payload: request,
    successAction: (response: AjaxResponse) =>
      upsertPersonBillingAccountFulfilled(
        request,
        {
          ...response.response,
          lastModified: response.response.lastModified,
        },
        !billingAccountId,
        successAction
      ),
    failureAction: upsertPersonBillingAccountFailed,
  });
}

export const upsertPersonBillingAccountsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  action$.pipe(
    ofType(TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT),
    concatMap((action: UpsertPersonBillingAccountAction) =>
      upsertPersonBillingAccount(
        action.request,
        epicDependencies,
        state$,
        action.billingAccountId,
        action.successAction
      )
    )
  );

const upsertPersonBillingAccountsFulfilledEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) =>
  action$.pipe(
    ofType(TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT_FULFILLED),
    mergeMap((action: UpsertPersonBillingAccountFulfilledAction) => {
      if (action.successAction) {
        action.successAction();
      }
      return EMPTY;
    })
  );

export const createOnlineOrder: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) => {
  return prepareUiApiRequest<ActionAndState>(
    action$.pipe(ofType(TypeKeys.SUBMIT_ONLINE_ORDER)),
    (action: ActionWithId) => actionToActionState(action, state$, 'onlineOrders')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const submitOrderAction = actionAndState.action as SubmitOnlineOrderAction;

      if (submitOrderAction.personBillingAddress && submitOrderAction.personBillingEmail) {
        return of(submitOrderAction)
          .pipe(
            concatMap((action: SubmitOnlineOrderAction | ErrorAction<TypeKeys>) => {
              if (action.type === TypeKeys.SUBMIT_ONLINE_ORDER) {
                const submitOnlineOrderAction = action as SubmitOnlineOrderAction;
                return upsertPersonBillingAccount(
                  {
                    address: submitOnlineOrderAction.personBillingAddress!,
                    email: submitOnlineOrderAction.personBillingEmail!,
                  },
                  epicDependencies,
                  state$,
                  submitOnlineOrderAction.personBillingAccountId
                );
              } else {
                return of(upsertPersonBillingAccountFailed('creating Personal Billing account failed', 500));
              }
            })
          )
          .pipe(
            concatMap((action: UpsertPersonBillingAccountFulfilledAction | ErrorAction<TypeKeys>): Action[] => {
              if (action.type === TypeKeys.UPSERT_PERSON_BILLING_ACCOUNT_FAILED) {
                return [submitOnlineOrderFailed('Failed to create Person billing account', 500)];
              } else {
                const fulfilledAction = action as UpsertPersonBillingAccountFulfilledAction;
                return [
                  fulfilledAction,
                  submitOnlineOrder(
                    submitOrderAction.items,
                    submitOrderAction.deliveryAddress,
                    submitOrderAction.contactPerson,
                    submitOrderAction.contactPersonValidationErrors,
                    submitOrderAction.customerType,
                    true,
                    submitOrderAction.billingAccountId,
                    submitOrderAction.addOns,
                    true,
                    submitOrderAction.deliveryMethodType,
                    submitOrderAction.approverContact,
                    submitOrderAction.approverContactValidationErrors,
                    fulfilledAction.response.billingAccountId,
                    undefined,
                    undefined,
                    submitOrderAction.deviceChangeRequest,
                    submitOrderAction.shipmentType,
                    submitOrderAction.voucherCode
                  ),
                ];
              }
            })
          );
      }

      // Make EPP Redeem API call
      if (submitOrderAction?.deviceChangeRequest?.deviceChangeOption === DeviceChangeOption.REDEEM) {
        const redeemRequestType = submitOrderAction?.deviceChangeRequest?.redeemRequestType;
        if (!redeemRequestType) {
          return [submitOnlineOrderFailed('Failed to redeem a device, redeem request type missing', 500)];
        }
        const eppRedeemPayload = {
          request: {
            requestType: redeemRequestType,
            employeeRedeem:
              redeemRequestType === EppRedeemTerminateRequestType.EMPLOYEE_REDEEM
                ? {
                    personBillingAccountId:
                      submitOrderAction?.deviceChangeRequest?.deviceChangeRedeemPrice !== 0
                        ? submitOrderAction.personBillingAccountId
                        : undefined,
                    approverContact: submitOrderAction.approverContact,
                  }
                : undefined,
            companyRedeem:
              redeemRequestType === EppRedeemTerminateRequestType.COMPANY_REDEEM
                ? {
                    billingAccountId: submitOrderAction?.deviceChangeRequest?.replacedSubscriptionBillingAccountId,
                  }
                : undefined,
          },
          subscriptionId: submitOrderAction?.deviceChangeRequest?.replacedSubscriptionId,
        } as PostEppRedeemTerminateAction;

        return postEppRedeemTerminate(eppRedeemPayload, state$, epicDependencies).pipe(
          concatMap((action: PostEppRedeemTerminateFulfilledAction | ErrorAction<TypeKeys>): Action[] => {
            if (action.type === TypeKeys.POST_EPP_REDEEM_TERMINATE_FAILED) {
              return [submitOnlineOrderFailed('Failed to redeem a device', 500)];
            } else {
              return [
                action,
                submitOnlineOrder(
                  submitOrderAction.items,
                  submitOrderAction.deliveryAddress,
                  submitOrderAction.contactPerson,
                  submitOrderAction.contactPersonValidationErrors,
                  submitOrderAction.customerType,
                  submitOrderAction.isCompanyNameRequired,
                  submitOrderAction.billingAccountId,
                  submitOrderAction.addOns,
                  true,
                  submitOrderAction.deliveryMethodType,
                  submitOrderAction.approverContact,
                  submitOrderAction.approverContactValidationErrors,
                  submitOrderAction.personBillingAccountId,
                  undefined,
                  undefined,
                  // Redeem already completed for this action; omit deviceChangeRequest so resubmission doesn't re-enter this branch
                  undefined,
                  submitOrderAction.shipmentType,
                  submitOrderAction.voucherCode
                ),
              ];
            }
          })
        );
      }

      // Create OnlineOrder stuff
      const orderItems: OnlineOrderItem[] = submitOrderAction.items
        .filter(
          item =>
            item.selectedCommercialProducts[0].commercialProduct.productType !== CommercialProductType.SALES_PRODUCT
        )
        .map(item => {
          const campaignAssociations = item.selectedCommercialProducts[0].commercialProduct.campaignAssociations;
          const campaignAssociationCode = item.selectedCampaignAssociation
            ? item.selectedCampaignAssociation.campaignAssociationCode
            : isCampaignPresent(campaignAssociations)
              ? campaignAssociations![0]?.campaignAssociationCode
              : undefined;
          const enrollmentProgramAlias =
            item.selectedCommercialProducts[0]?.enrollmentProgramAlias !== ''
              ? {
                  enrollmentProgramAlias: item.selectedCommercialProducts[0].enrollmentProgramAlias,
                }
              : undefined;

          /**
           * Some AddOns are added to the order without them being present on the CommercialProduct.
           * We need to add these to order separately so they won't be left out.
           * For example Yrityspuhe voice sub "minute ordering" needs this.
           * Some other places are adding AddOns to the CP by mutating it such as Laitenetti product.
           */
          const extraAddOns =
            item.selectedCommercialProducts[0].addedAddOns
              ?.filter(
                addedAddOn =>
                  item.selectedCommercialProducts[0].commercialProduct.addOnAssociations?.find(
                    addOnAssociation => addOnAssociation.addOnCode === addedAddOn.addOnCode
                  ) === undefined
              )
              .map(addedAddOn => {
                return {
                  addOnAssociationCode: addedAddOn.addOnAssociationCode,
                  addOnCode: addedAddOn.addOnCode,
                  campaignAssociationCode: undefined,
                  addOnAttributes: addedAddOn.addOnAttributes,
                };
              }) || [];

          let orderItem: OnlineOrderItem = {
            // Add default addons (by_default + selected) and also add extra addons
            addOns: [
              ...getDefaultAddOns(item.selectedCommercialProducts[0], submitOrderAction.addOns, campaignAssociations),
              ...extraAddOns,
            ],
            // only supporting one selected commercial product per selected offer (as does the UI) for now
            commercialProductCode: item.selectedCommercialProducts[0].commercialProduct.commercialProductCode,
            commercialProductName: item.selectedCommercialProducts[0].commercialProduct.commercialProductName,
            campaignAssociationCode,
            offerCode: item.offer.offerCode,
            orderItemQuantity: item.selectedCommercialProducts.length,
            orderItemUnits: item.selectedCommercialProducts.map((product, index) =>
              mapCommercialProductToOnlineOrderItemUnit(
                product,
                index,
                submitOrderAction.deliveryMethodType,
                submitOrderAction.contactPerson
              )
            ),
            enrollmentProgramSelected: item.selectedCommercialProducts[0].enrollmentProgramConsent,
            ...enrollmentProgramAlias,
          };

          if (item.selectedCommercialProducts[0].replacedSubscriptionId) {
            orderItem = {
              ...orderItem,
              replacedSubscriptionId: item.selectedCommercialProducts[0].replacedSubscriptionId,
            };
          }
          return orderItem;
        });

      const salesProductsOrderItems: SalesProductOrderItem[] = getSalesProductsOrderItems(
        submitOrderAction.items,
        submitOrderAction.deliveryMethodType
      );

      const totalSums = getTotalSums(
        submitOrderAction.items
          .map(configuredOffer => configuredOffer.selectedCommercialProducts)
          .reduce((prev, next) => prev.concat(next), []) // flattens
      );

      const catalogCode = isEoe(window.location.pathname) ? getCatalogCode() || undefined : undefined;

      const isPersonBaRequired = totalSums.totalMonthlyRecurringCharge > 0 || totalSums.totalOneTimeCharge > 0;
      // We cannot utilize PostOnlineOrderRequest here because it contains a lot of attributes
      // that are populated in chameleon-rest or sfdc
      const onlineOrder: Partial<OnlineOrder> = {
        approverContact: submitOrderAction.approverContact,
        billingAccountId: submitOrderAction.billingAccountId,
        catalogCode: catalogCode,
        deliveryAddress: submitOrderAction.deliveryAddress,
        orderItems: orderItems,
        salesProductsOrderItems: salesProductsOrderItems,
        personBillingAccountId: isPersonBaRequired ? submitOrderAction.personBillingAccountId : undefined,
        totalFixedTermCharge: totalSums.totalFixedTermCharge,
        totalMonthlyRecurringCharge: totalSums.totalMonthlyRecurringCharge,
        totalOneTimeCharge: totalSums.totalOneTimeCharge,
        totalOneTimeChargeVat: totalSums.totalOneTimeChargeVat,
        shoppingCartId: isEoe(window.location.pathname)
          ? getShoppingCartId()
          : parseBasketJson(getShoppingBasketFromLocalStorage()).id,
        // Fall back to Total delivery so shipmentType is never silently omitted from the order payload, see OFI-66328
        shipmentType: submitOrderAction.shipmentType ?? OnlineOrder.ShipmentTypeEnum.TOTAL_DELIVERY,
        voucherCode: submitOrderAction.voucherCode,
      };
      return callUiApi({
        epicDependencies,
        failureAction: submitOnlineOrderFailed,
        method: createOnlineOrderPrivateMethod(),
        payload: {
          onlineOrder: onlineOrder,
        },
        state$,
        successAction: submitOnlineOrderFulfilled,
        headers: getMdmIdHeaderFromUrlParams(),
      }).pipe(
        concatMap(action => {
          const submitOrderFulfilledAction = action as SubmitOrderFulfilledAction;
          if (submitOrderFulfilledAction.type === TypeKeys.SUBMIT_ONLINE_ORDER_FULFILLED) {
            return [action, pollForCreditCheck(submitOrderFulfilledAction.orderId)];
          }
          return of(action);
        })
      );
    })
  );
};

export const pollCreditCheck: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest<ActionAndState>(action$.pipe(ofType(TypeKeys.POLL_FOR_CREDIT_CHECK)), (action: ActionWithId) =>
    actionToActionState(action, state$, 'onlineOrders')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const pollForCreditCheckAction = actionAndState.action as PollForCreditCheckAction;
      const queryParams: { [s: string]: string } = {};
      if (pollForCreditCheckAction.orderId) {
        queryParams.onlineOrderId = pollForCreditCheckAction.orderId;
      }
      const request = {
        basePath: UI_API_PRIVATE_BASE_PATH,
        epicDependencies,
        failureAction: pollForCreditCheckFailed,
        path: '/online-orders/credit-check',
        queryParams: queryParams,
        state$,
        successAction: (response: CreditCheckResponse) => {
          const fulfilledAction = pollForCreditCheckV2Fulfilled(response, pollForCreditCheckAction.orderId);
          if (response.decision === CreditCheckDecision.PASS) {
            return [fulfilledAction, push(paths.EMPLOYEE_DEVICE_CHECKOUT_THANK_YOU)];
          }
          return [fulfilledAction];
        },
      };
      // TODO: Use uiApiUtils.callGetUiApiWithRetry instead
      return getFromUiApiWithRetry(request, 2000, {
        ...getPrivateUiApiHeaders(),
        ...getMdmIdHeaderFromUrlParams(),
      });
    })
  );

export const onlineOrdersEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  createOnlineOrder,
  pollCreditCheck,
  upsertPersonBillingAccountsEpic,
  upsertPersonBillingAccountsFulfilledEpic
);
